import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from './use-api-client.js';
import type { MessageConversation } from '../api/types.js';

// Messages to paired servers, and the pairings (ADR-025 step 2); the conversations allowed for an hour (step 3)

export function useOutboxMessages(serverId: string) {
  const api = useApiClient();
  return useQuery({
    queryKey: ['outbox-messages', serverId],
    queryFn: () => api.outbox.messages(serverId),
    select: (d) => d.messages,
    refetchInterval: 30_000,
    refetchOnWindowFocus: 'always',
  });
}

export function useDecideMessage(serverId: string) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; decision: 'send' | 'reject'; sessionId?: string | null; allowConversation?: boolean; start?: boolean }) =>
      v.decision === 'send'
        ? api.outbox.send(serverId, v.id, v.sessionId ?? null, { ...(v.allowConversation ? { allowConversation: true } : {}), ...(v.start ? { start: true } : {}) })
        : api.outbox.reject(serverId, v.id),
    onSettled: (_d, _e, v) => {
      void qc.invalidateQueries({ queryKey: ['outbox-messages', serverId] });
      void qc.invalidateQueries({ queryKey: ['inbox', serverId] });
      if (v.allowConversation) void qc.invalidateQueries({ queryKey: ['conversations'] });
      if (v.start) void qc.invalidateQueries({ queryKey: ['servers'] });
    },
  });
}

/** The conversations of this server's sessions the operator allowed, still running (ADR-025 step 3) */
export function useConversations(serverId: string) {
  const api = useApiClient();
  return useQuery({
    queryKey: ['conversations', serverId],
    queryFn: () => api.outbox.conversations(serverId),
    select: (d) => d.conversations,
    refetchInterval: 60_000,
  });
}

export function useStopConversation(serverId: string) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (c: MessageConversation) => api.outbox.stopConversation(serverId, c),
    // Kept on both sides
    onSettled: () => void qc.invalidateQueries({ queryKey: ['conversations'] }),
  });
}

export function usePeers(serverId: string) {
  const api = useApiClient();
  return useQuery({ queryKey: ['peers', serverId], queryFn: () => api.outbox.peers(serverId), select: (d) => d.peers });
}

export function useSetPeers(serverId: string) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (peers: string[]) => api.outbox.setPeers(serverId, peers),
    onSuccess: (d) => {
      qc.setQueryData(['peers', serverId], d);
      // The pairing is kept on both sides
      void qc.invalidateQueries({ queryKey: ['peers'] });
    },
  });
}
